import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { HammerIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Card,
	Checkbox,
	FlexGap,
	Heading3,
	Paragraph,
	Progress,
	PropertyChip,
	QuillText,
	Tooltip,
	tokens,
} from '@treams/treams-ui';

const Percentage = styled(Paragraph)`
	width: 36px;
	flex-shrink: 0;
	text-align: right;
	white-space: nowrap;
`;

function GoalCompetency(props: { competency: graphql.CompetencyGoal }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const option = props.competency.competency.levels[props.competency.competencyLevelIndex];
	const title = localize(props.competency.competency.title).text;
	const level = option ? localize(option.level.title).text : undefined;
	const label = level ? `${title} · ${level}` : title;

	return (
		<Box mt={`${tokens.gap.element}px`}>
			<Tooltip
				portal
				position="top"
				content={
					<Box style={{ whiteSpace: 'normal' }}>
						<QuillText
							value={localize(option?.criteria ?? [])?.text}
							placeholder={t('evaluation.submission.competency.levels.noDescription')}
						/>
						{level && (
							<Paragraph small mt={`${tokens.gap.inline}px`} style={{ textAlign: 'center' }}>
								{level}
							</Paragraph>
						)}
					</Box>
				}
			>
				<PropertyChip icon={<HammerIcon />} label={label} />
			</Tooltip>
		</Box>
	);
}

const GoalSelectorItem = (props: {
	goal:
		| graphql.Goal
		| graphql.FeedbackRequestGoal
		| graphql.GoalRevision
		| graphql.EvaluationSubmissionGoal;
	selected?: boolean;
	onChange?: (selected: boolean, text?: string) => void;
}) => {
	const { localize } = useLocalize();
	const selected = props.selected ?? false;

	return (
		<Card
			selected={selected}
			onClick={() => props.onChange?.(!selected)}
			p={`${tokens.padding.compact}px ${tokens.padding.control}px`}
			chevron={false}
			width="100%"
		>
			<Flex alignItems="center">
				<Box flexGrow={1} flexShrink={1} minWidth={0}>
					<FlexGap flexDirection="column" gap={tokens.gap.inline}>
						<Heading3>{localize(props.goal.title!).text}</Heading3>
						{props.goal.dueDate && (
							<Paragraph small>{moment(props.goal.dueDate).format('L')}</Paragraph>
						)}
					</FlexGap>
				</Box>
				<FlexGap
					gap={tokens.gap.element}
					alignItems="center"
					flexShrink={0}
					ml={`${tokens.gap.element}px`}
				>
					<Progress squared width="80px" value={props.goal.progress} />
					<Percentage small>{(props.goal.progress * 100).toFixed(0)}%</Percentage>
					<Box
						flexShrink={0}
						onClick={(event) => {
							event.stopPropagation();
							props.onChange?.(!selected);
						}}
					>
						<Checkbox checked={selected} />
					</Box>
				</FlexGap>
			</Flex>
			{props.goal.competency && <GoalCompetency competency={props.goal.competency} />}
		</Card>
	);
};

export default GoalSelectorItem;
